import React, { useRef, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import initExporting from "highcharts/modules/exporting";
import initOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import { resolveTemplate } from "../Utils/resolveTemplate";
import { useChartSync } from "../Contexts/ChartSyncContext";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";

// Needed for SVG→PNG capture in PDF export (idempotent, same as the other chart widgets).
initExporting(Highcharts);
initOfflineExporting(Highcharts);

const { Text } = Typography;

const CHART_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

export function ScatterplotWidget({ id }: { id: string }) {
  const data = useWidgetData(id);
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const syncContext = useChartSync();
  const requestSyncRef = useRef<() => void>(() => {});
  if (syncContext) requestSyncRef.current = syncContext.requestSync;

  const slicerValues = useSlicerVariables();
  const rawTitle = data?.type === "scatterplot" ? (data.spec.display.title || null) : null;
  const chartTitle = rawTitle ? resolveTemplate(rawTitle, slicerValues).resolved : null;

  const options = useMemo(() => {
    if (!data || data.type !== "scatterplot") return null;
    const base: Record<string, unknown> = {
      ...data.scatterplot.chartOptions,
      chart: {
        ...((data.scatterplot.chartOptions.chart as Record<string, unknown>) ?? {}),
        reflow: true,
        events: {
          load() {
            requestSyncRef.current();
          },
        },
      },
      title: { text: "" },
    };

    // Preserve legend-toggled visibility across options updates
    const chart = chartRef.current?.chart;
    if (chart && Array.isArray(base.series)) {
      const hiddenNames = new Set(
        chart.series.filter((s: any) => !s.visible).map((s: any) => s.name),
      );
      if (hiddenNames.size > 0) {
        base.series = (base.series as any[]).map((s: any) =>
          hiddenNames.has(s.name) ? { ...s, visible: false } : s,
        );
      }
    }

    return base;
  }, [data]);

  useEffect(() => {
    if (!syncContext) return;
    const getCell = () =>
      containerRef.current?.closest(".grid-item-wrapper") ?? containerRef.current ?? null;
    const getChart = () => chartRef.current?.chart ?? null;

    syncContext.register(id, getCell, getChart);
    const t = setTimeout(() => syncContext.requestSync(), 200);

    return () => {
      clearTimeout(t);
      syncContext.unregister(id);
    };
  }, [id, syncContext]);

  if (!data || data.type !== "scatterplot" || !options) {
    return (
      <div className="chart-widget" data-grid-id={id} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No scatterplot data" />
      </div>
    );
  }

  const hasData = data.scatterplot.pointCount > 0;
  const droppedUnpaired = data.scatterplot.droppedUnpaired;

  return (
    <div ref={containerRef} className="chart-widget" data-grid-id={id}>
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {chartTitle && <Text strong style={{ fontSize: 18 }}>{chartTitle}</Text>}
          <WidgetInfoButton id={id} />
        </span>
      </div>
      <div className="chart-widget-body" style={{ position: "relative" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
          containerProps={{ style: CHART_CONTAINER_STYLE }}
        />
        {!hasData && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {droppedUnpaired > 0
                ? `No paired data in the specified date range (${droppedUnpaired} single-axis days)`
                : "No data found in the specified date range"}
            </Text>
          </div>
        )}
        {hasData && droppedUnpaired > 0 && (
          <Text
            type="secondary"
            style={{ position: "absolute", right: 8, bottom: 2, fontSize: 10, pointerEvents: "none" }}
          >
            {droppedUnpaired} single-axis {droppedUnpaired === 1 ? "day" : "days"} hidden
          </Text>
        )}
      </div>
    </div>
  );
}
